<template>
  <SogPage :title="_title">
    <SogFlexRow>
      <SogCard v-show="_isAusziffernVisible">
        <SogCardTitle>Letzte Auszifferung</SogCardTitle>
        <SogCardContent>
          um
          <SogDataLabel
            :value="_auszieffernAm"
            :value-type="SogDataLabelValueType.DateTime"
          />
          <br>
          <SogDataLabel
            :value="_auszieffernPerson"
          />
        </SogCardContent>
        <SogCardActions>
          <SogCardActionButton @click="onClickAusziffernNow">
            Jetzt
          </SogCardActionButton>
        </SogCardActions>
      </SogCard>
      <SogCard>
        <SogCardActions>
          <SogCardActionButton
            v-show="_selectedOrderId"
            mdi-icon="mdi-information"
            @click="onDetails"
          />
          <SogCardActionButton
            v-show="_selectedOrderId"
            mdi-icon="mdi-printer"
            @click="onPrint"
          />
          <SogCardActionButton
            v-show="_selectedOrderId"
            @click="onShowInsurance"
          >
            Versicherungskarten
          </SogCardActionButton>
          <SogTextBox
            v-model="_listPage.number"
            placeholder="Nummer"
            :interval-modified="onSearch"
          />
          <SogDropDownList
            v-show="_filterType !== 'person'"
            v-model="_personId"
            placeholder="Name"
            :data-source="_lstPerson"
            :fields="_fieldsPerson"
          />
          <div
            v-show="_type==='Invoice'"
            style="width: 80px"
          >
            <SogDropDownList
              v-model="_overdue"
              placeholder="überfällig"
              :data-source="_lstYes"
              :fields="_fields"
              popup-width="80px"
            />
          </div>
          <SogCardActionButton
            :kind="SogButtonKind.Search"
            style="align-self: flex-end"
            @click="onSearch"
          />
          <SogCardActionButton
            mdi-icon="mdi-file-excel"
            @click="onExportExcel"
          />
          <SogCardActionButton
            :kind="SogButtonKind.Back"
            style="align-self: flex-end"
          />
        </SogCardActions>
        <SogCardContent>
          <OrderListComponent
            ref="_listComponent"
            @selected="onSelected"
          />
        </SogCardContent>
      </SogCard>
    </SogFlexRow>
  </SogPage>
</template>

<script setup lang="ts">
import SogPage from "@/shared/ui/SogPage.vue";
import {onBeforeRouteUpdate, RouteLocationNormalizedLoaded, useRoute, useRouter} from "vue-router";
import {useAccountStore} from "@/stores/accountStore";
import {computed, onMounted, reactive, ref, watch} from "vue";
import SogCardContent from "@/shared/ui/SogCardContent.vue";
import SogCardActions from "@/shared/ui/SogCardActions.vue";
import SogCardActionButton from "@/shared/ui/SogCardActionButton.vue";
import SogCard from "@/shared/ui/SogCard.vue";
import {useNavigationStore} from "@/modules/Navigation/store";
import apiOrder from "@/modules/Order/api";
import apiFile from "@/shared/services/apiFile";
import SogTextBox from "@/shared/ui/SogTextBox.vue";
import OrderListComponent from "@/modules/Order/components/OrderListComponent.vue";
import {SogButtonKind} from "@/shared/ui/types/SogButtonKind";
import SogDropDownList from "@/shared/ui/SogDropDownList.vue";
import {useSpinner} from "@/shared/composables/SogSpinner";
import {addDays} from "@/shared/utils/DateUtils";
import {IdType} from "@/shared/model/idType";
import {useOrderStore} from "@/modules/Order/store";
import {storeToRefs} from "pinia";
import Person from "@/shared/model/person";
import SogCardTitle from "@/shared/ui/SogCardTitle.vue";
import SogDataLabel from "@/shared/ui/SogDataLabel.vue";
import {SogDataLabelValueType} from "@/shared/ui/types/SogDataLabelValueType";
import SogFlexRow from "@/shared/ui/SogFlexRow.vue";
import ConfigItem from "@/shared/model/ConfigItem";
import delay from "@/shared/utils/delay";


const _router = useRouter()
const _config = useAccountStore()
const _orderStore = useOrderStore();
const _spinner = useSpinner()
const _title = ref("");
const _navigationStore = useNavigationStore()
const _listComponent = ref<InstanceType<typeof OrderListComponent> | null>(null)

const _sector = ref("");
const _type = ref("");
const _filterType = ref("");
const _selectedOrderId = ref<string | undefined>(undefined);
const _selectedOrderNumber = ref<string | undefined>(undefined);
const _lstYes = reactive([{id: "yes", value: "Ja"}, {id: "all", value: "Alle"}]);
const _personAll = new Person();
const _lstPerson = ref<Person[]>([_personAll]);
const _fields = {text: 'value', value: 'id'}
const {listPage: _listPage} = storeToRefs(_orderStore)
const _overdue = ref(_listPage.value.overdue)
const _personId = ref(_listPage.value.personId)
const _fieldsPerson = {text: 'personName', value: 'id'}
const _auszieffernAm = ref<Date|undefined>();
const _auszieffernPerson = ref("")

_personAll.id = "all";
_personAll.personName = "Alle";

function p_setTitle() {
  _title.value = "";
  if (_sector.value === "purchase") {
    _title.value = "Eingangs"
  }
  if (_sector.value === "sale") {
    _title.value = "Ausgangs"
  }
  if (_type.value === "Order") {
    _title.value += "bestellungen"
  }
  if (_type.value === "Invoice") {
    _title.value += "rechnungen"
  }
  if (_filterType.value === "person" && _navigationStore.currentPersonId) {
    _title.value += " für " + _navigationStore.currentPersonName
  }
}


const _isAusziffernVisible = computed(()=>{
  return _config.isRoot && _sector.value==='sale'&& _type.value==='Invoice';
})


async function init(route: RouteLocationNormalizedLoaded) {
  await p_loadPersons();
  _sector.value = route.params.sector as string;

  if (!_config.hasAction(_sector.value)) {
    await _router.push("/")
    return
  }


  _type.value = route.params.type as string;
  _filterType.value = route.params.filtertype as string;
  _selectedOrderId.value = "";
  _overdue.value = _type.value==='Invoice'?_listPage.value.overdue:"all"
  _personId.value = _listPage.value.personId

  p_loadAusziffern();


  p_setTitle()
  onSearch()

}

onMounted(() => {
  const route = useRoute();
  init(route)
})

onBeforeRouteUpdate((to) => {
  init(to)
});




function onShowInsurance() {
  if (_selectedOrderId.value) {
    _navigationStore.showInsuranceList(IdType.Order, _selectedOrderId.value)
  }
}

async function onDetails() {
  if (_selectedOrderId.value) {
    await _navigationStore.showObject(IdType.Order, _selectedOrderId.value);
  }
}

async function onPrint() {
  if (_selectedOrderId.value) {
    try {
      _spinner.show();
      const id = await apiOrder.orderInitPdf(_selectedOrderId.value)
      if (id) {
        await apiFile.downloadFile(id, _selectedOrderNumber.value + ".pdf")
      }
    } finally {
      _spinner.hide();
    }
  }
}

function onSearch() {
  _listComponent.value?.adaptor.removePredicate("orderNumber");
  if (_listPage.value.number)
    _listComponent.value?.adaptor.addPredicate("orderNumber", "contains", _listPage.value.number.toUpperCase());

  _listComponent.value?.adaptor.removePredicate("paid");
  _listComponent.value?.adaptor.removePredicate("ordereddate");
  if (_overdue.value === "yes") {
    _listComponent.value?.adaptor.addPredicate("paid", "equal", "false");
    const t_date: Date = addDays(new Date(), -15)
    _listComponent.value?.adaptor.addPredicate("ordereddate", "lessthan", t_date);
  }

  if(_filterType.value !== "person") {
    _listComponent.value?.adaptor.removePredicate("personId");
    if (_personId.value !== "all") {
      _listComponent.value?.adaptor.addPredicate("personId", "equal", _personId.value);
    }
  }

  _listComponent.value?.refresh();
}


watch(_overdue, () => {
      _listPage.value.overdue = _overdue.value
      onSearch()
    }
)

watch(_personId, () => {
      _listPage.value.personId = _personId.value
      onSearch()
    }
)

function onExportExcel() {
  _listComponent.value?.excelExport()
}

function onSelected(data: any | undefined) {
  _selectedOrderId.value = data?.id;
  _selectedOrderNumber.value = data?.orderNumber
}

async function p_loadPersons() {
  const t_res = await apiOrder.personList()
  if(t_res)
    _lstPerson.value = [_personAll,...t_res];
}

async function onClickAusziffernNow() {

  const t_item = new ConfigItem();
  t_item.key = "AusziffernDatum";
  t_item.setAsDateTime(new Date());

  const t_updateDate = apiOrder.updateConfig(t_item);
  t_item.key = "AusziffernPerson";
  t_item.value = _config.actionPerson?.getFullPersonName();
  const t_updatePerson = apiOrder.updateConfig(t_item);

  await Promise.all([t_updateDate, t_updatePerson]);
  await p_loadAusziffern()
}

async function p_loadAusziffern() {
  if(!_isAusziffernVisible.value)
    return;

  const t_res = await apiOrder.loadConfig(["AusziffernDatum","AusziffernPerson"]);
  if (t_res) {
    _auszieffernAm.value = undefined;
    _auszieffernPerson.value = ""
    t_res.forEach(t=>{
      if(t.key === "AusziffernDatum"){
        _auszieffernAm.value = t.getAsDateTime()
      }
      else if(t.key === "AusziffernPerson"){
        _auszieffernPerson.value = t.value??""
      }
    })
  }
}

</script>
<style scoped>
</style>
